import React, { useRef, useState, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { Menu } from "primereact/menu";
import { useSelector } from "react-redux";

import FileIcon from "~/components/file-type-icon";
import DocumentDescription from "~/components/document-description";
import {
  downloadFileHandler,
  openPreviewInNewTab,
  openFileInNewTab,
  resolveDisplayFileName,
  getOriginalFileName,
  getBackendFileName,
} from "~/utils/fileUtils";
const NO_DESCRIPTION_PLACEHOLDER = "No description available";
const DOCUMENT_TABLE_SORT_FIELDS = {
  NAME: "displayName",
  DESCRIPTION: "description",
  SUBCATEGORY: "subCategory",
  LAST_MODIFIED: "createdAt",
};
import Categories from "~/components/Processes/conversations/utils/categories.json";
import EmptyDataView from "~/components/nodataview";
import fileIcon from "~/assets/empty-file.svg";
import "~/Pages/Process/components/tabs/attachments/attachments.scss";
import { Badge } from "primereact/badge";
import { useNavigate } from "react-router-dom";

const getSubCategoryName = (categoryCode, subCategoryCode) => {
  const cat = Categories?.find((c) => c.code === categoryCode);
  const sub = cat?.subCategory?.find((s) => s.code === subCategoryCode);
  return sub?.name ?? subCategoryCode ?? "-";
};

const getContainer = (blobUrl) =>
  blobUrl?.includes("/ess") ? "ess" : "documents";

const safeDecode = (text) => {
  if (text == null || text === "") return text;
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
};

export default function DocumentTable({
  value = [],
  loading = false,
  formatDate,
  onDelete,
  relatedId,
  relatedType,
  totalRecords = 0,
  rows = 10,
  first = 0,
  onPageChange,
  onSortOrderChange,
  isFilterApplied = false,
  isPayslipFolder = false,
  emptyMessage = "No files found",
  filterEmptyHeader = "It looks like there are no results based on the filters you applied",
  filterEmptyDescription = "No matches were found. Please adjust your filters to improve your search.",
  /** Custom download handler; if provided, used instead of default. Signature: (row) => Promise|void */
  onDownload,
  /** Optional subtext rendered below file name, e.g. breadcrumb. Signature: (row) => ReactNode */
  nameSubtext,
  /** Called after description is updated; use to refetch the list. */
  onRefetch,
  /** When true, hide the details sidebar and related actions (e.g. onboarding/creation flow). */
  hideDetailsSidebar = false,
  publishHandler,
  unpublishHandler,
  entityId,
  service,
  /** When true, show backend/original file name instead of subcategory-based display name (Employee Registry, Payroll process out of scope). */
  useOriginalFileName = false,
  /** When true, show backend storage name (attachmentData.name from API) for Payroll service line. */
  useBackendFileName = false,
}) {
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailsRow, setDetailsRow] = useState(null);
  const [rowOverrides, setRowOverrides] = useState({});

  const getRowKey = (row) => row?.blobName || row?.blob_url || row?.id || "";

  const applyOverrides = (row) => {
    if (!row) return row;
    const key = getRowKey(row);
    if (!key) return row;
    const override = rowOverrides[key];
    if (!override) return row;
    return { ...row, ...override };
  };
  const { id: userId, role } = useSelector((state) => state.login);
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState(1);

  const canDelete = (row) =>
    role?.startsWith("GXM") ||
    (!isPayslipFolder && row.created_by === userId) ||
    (row?.isPayslipData && role?.includes(row.owner)) ||
    (isPayslipFolder && row.user_id === userId);

  const handleMenuToggle = (e, rowData) => {
    setSelectedRow(rowData);
    menuRef.current?.toggle(e);
  };

  const container = (row) => getContainer(row?.blob_url);
  const openPreview = (row) => {
    const objectURL = row?.blob_url || row?.objectURL;
    if (!objectURL) return;
    if (objectURL.startsWith("blob:") || objectURL.startsWith("data:")) {
      return openFileInNewTab({ objectURL });
    }
    return openPreviewInNewTab({ objectURL }, container(row));
  };

  const download = (row) => {
    if (onDownload) {
      onDownload(row);
    } else {
      downloadFileHandler(
        { blob_url: row?.blob_url, name: row?.name },
        container(row),
      );
    }
  };
  const menuItems = [
    {
      label: "Preview File",
      icon: "pi pi-eye",
      command: () => selectedRow && openPreview(selectedRow),
    },
    ...(!hideDetailsSidebar
      ? [
          {
            label: "View File Details",
            icon: "pi pi-info-circle",
            command: () => {
              if (selectedRow) {
                setDetailsRow(applyOverrides(selectedRow));
                setSelectedRow(null);
              }
            },
          },
        ]
      : []),
    ...(["Entities +"].includes(service) && relatedType === "PROCESS"
      ? !selectedRow?.is_published
        ? [
            {
              label: "Publish to Entity Files",
              icon: "pi pi-external-link",
              command: () => {
                publishHandler(true, selectedRow);
              },
            },
          ]
        : relatedType === "PROCESS"
          ? [
              {
                label: "View in Core Entity Folder",
                icon: "pi pi-external-link",
                command: () => {
                  navigate(
                    `/files/entity/${entityId}/services/a9c07a03-326c-45e5-b644-a795f0cbe67r/entity-information`,
                    {
                      state: {
                        id: selectedRow?.category,
                        code: selectedRow?.category,
                        name: Categories.find(
                          (c) => c.code === selectedRow?.category,
                        )?.name,
                        isCategoryFolder: true,
                      },
                    },
                  );
                },
              },
            ]
          : []
      : []),
    ...(selectedRow?.is_published && relatedType
      ? [
          {
            label: "Unpublish from Entity Files",
            icon: "pi pi-external-link",
            command: () => {
              unpublishHandler(true, selectedRow);
            },
          },
        ]
      : []),

    {
      label: "Download",
      icon: "pi pi-download",
      command: () => selectedRow && download(selectedRow),
    },
    ...(selectedRow && canDelete(selectedRow)
      ? [
          { separator: true },
          {
            label: "Delete File",
            icon: "pi pi-trash",
            command: () => onDelete?.(selectedRow),
          },
        ]
      : []),
  ];
  const enhancedValue = useMemo(() => {
    return value.map((file) => {
      const key = getRowKey(file);
      const override = key ? rowOverrides[key] : undefined;
      const effectiveFile = override ? { ...file, ...override } : file;
      return {
        ...effectiveFile,
        displayName: useBackendFileName
          ? getBackendFileName(effectiveFile)
          : useOriginalFileName
            ? getOriginalFileName(effectiveFile)
            : resolveDisplayFileName(effectiveFile, value, getSubCategoryName),
      };
    });
  }, [value, useOriginalFileName, useBackendFileName, rowOverrides]);
  const useServerSort = Boolean(onSortOrderChange);
  const sortedValue = useMemo(() => {
    if (useServerSort || !enhancedValue?.length || !sortField)
      return enhancedValue;
    const order = sortOrder === 1 ? "ASC" : "DESC";
    return [...enhancedValue].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === DOCUMENT_TABLE_SORT_FIELDS.DESCRIPTION) {
        aVal = (aVal && String(aVal).trim()) || "\uFFFF";
        bVal = (bVal && String(bVal).trim()) || "\uFFFF";
      } else {
        aVal = aVal ?? "";
        bVal = bVal ?? "";
      }
      const cmp = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
      });
      return order === "ASC" ? cmp : -cmp;
    });
  }, [enhancedValue, sortField, sortOrder, useServerSort]);

  const skeletonRows = [...Array(5)];

  if (loading) {
    return (
      <DataTable
        value={skeletonRows}
        tableStyle={{ minWidth: "50rem" }}
        className="datatable-right-align"
      >
        <Column header="Name" body={() => <Skeleton width="100%" />} />
        <Column header="Description" body={() => <Skeleton />} />
        <Column header="Subcategory" body={() => <Skeleton />} />
        <Column header="Last Modified" body={() => <Skeleton />} />
        <Column body={() => <Skeleton />} />
      </DataTable>
    );
  }

  if (isFilterApplied && !value?.length) {
    return (
      <EmptyDataView
        Icon={<img src={fileIcon} alt="File Icon" />}
        Header={filterEmptyHeader}
        Description={filterEmptyDescription}
      />
    );
  }
  return (
    <>
      <DataTable
        value={sortedValue}
        tableStyle={{ width: "100%", minWidth: "50rem" }}
        className="datatable-right-align"
        paginator={
          (totalRecords > 0 ? totalRecords : sortedValue.length) > rows
        }
        rows={rows}
        rowsPerPageOptions={[10, 20, 30]}
        first={first}
        totalRecords={
          totalRecords > 0 && totalRecords !== sortedValue.length
            ? totalRecords
            : undefined
        }
        lazy={totalRecords > 0 && totalRecords !== sortedValue.length}
        onPage={onPageChange}
        emptyMessage={emptyMessage}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={(e) => {
          setSortField(e.sortField);
          setSortOrder(e.sortOrder);
          onSortOrderChange?.(e.sortOrder === 1 ? "ASC" : "DESC");
        }}
      >
        <Column
          header="Name"
          sortable
          sortField={DOCUMENT_TABLE_SORT_FIELDS.NAME}
          body={(row) => {
            const effectiveRow = applyOverrides(row);
            return (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <div className="file__name">
                  <FileIcon
                    isPayslipFolder={isPayslipFolder}
                    filetype={
                      isPayslipFolder
                        ? "application/pdf"
                        : effectiveRow?.file_type
                    }
                    filename={effectiveRow.displayName || effectiveRow?.name}
                    downloadFile={() => openPreview(row)}
                  />
                  {row.is_published && (
                    <Badge
                      className="publish-badge"
                      style={{
                        width: "200px",
                        height: "24px",
                      }}
                      value={
                        relatedType === "PROCESS"
                          ? "Publish to Entity Files"
                          : "Published via Process"
                      }
                    />
                  )}
                </div>
                {nameSubtext && nameSubtext(row)}
              </div>
            );
          }}
        />
        <Column
          header="Description"
          sortable
          sortField={DOCUMENT_TABLE_SORT_FIELDS.DESCRIPTION}
          body={(row) => {
            const effectiveRow = applyOverrides(row);
            const displayDesc = effectiveRow?.description;
            return (
              <div className="flex align-items-center justify-content-between">
                <span className="file-name-ellipsis">
                  {displayDesc?.trim()
                    ? safeDecode(displayDesc)
                    : NO_DESCRIPTION_PLACEHOLDER}
                </span>
                {!hideDetailsSidebar && (
                  <span
                    className="pi pi-angle-right cursor-pointer"
                    onClick={() => setDetailsRow(applyOverrides(row))}
                    role="button"
                    aria-label="View description details"
                  />
                )}
              </div>
            );
          }}
        />
        <Column
          header="Subcategory"
          sortable
          sortField={DOCUMENT_TABLE_SORT_FIELDS.SUBCATEGORY}
          body={(row) => {
            const effectiveRow = applyOverrides(row);
            return (
              <span
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  cursor: "default",
                }}
              >
                {getSubCategoryName(
                  effectiveRow?.category,
                  effectiveRow?.subCategory,
                )}
              </span>
            );
          }}
        />
        <Column
          header="Last Modified"
          sortable
          sortField={DOCUMENT_TABLE_SORT_FIELDS.LAST_MODIFIED}
          body={(row) => {
            const effectiveRow = applyOverrides(row);
            return (
              <span>
                {formatDate?.(
                  effectiveRow?.lastModified ||
                    effectiveRow?.updatedAt ||
                    effectiveRow?.createdAt,
                ) ?? "-"}
              </span>
            );
          }}
        />
        <Column
          className="user_action--col"
          body={(row) => (
            <div className="flex align-items-center">
              {!hideDetailsSidebar && (
                <span
                  className="pi pi-info-circle cursor-pointer pencil-pointer"
                  onClick={() => setDetailsRow(applyOverrides(row))}
                  role="button"
                  aria-label="View file details"
                />
              )}
              <span
                className="pi pi-download pencil-pointer"
                onClick={() => download(row)}
                role="button"
                aria-label="Download"
              />
              <Menu
                model={menuItems}
                popup
                ref={menuRef}
                id={`doc_menu_${row?.id ?? row?.name}`}
              />
              {canDelete(row) && (
                <span
                  className="pi pi-ellipsis-v pencil-pointer"
                  onClick={(e) => handleMenuToggle(e, row)}
                  role="button"
                  aria-haspopup
                  aria-controls={`doc_menu_${row?.id ?? row?.name}`}
                  aria-label="Actions"
                />
              )}
            </div>
          )}
        />
      </DataTable>

      {!hideDetailsSidebar && detailsRow && (
        <DocumentDescription
          data={detailsRow}
          id={relatedId}
          type={relatedType}
          visible={!!detailsRow}
          onRefetch={onRefetch}
          onVisibleChange={(v) => !v && setDetailsRow(null)}
          hideTrigger
          onDataUpdate={(updatedRow) => {
            const key = getRowKey(detailsRow);
            setDetailsRow((prev) => ({ ...prev, ...updatedRow }));
          }}
          onDescriptionUpdated={(row, newDesc) => {
            const key = getRowKey(row);
            if (key) {
              const patch =
                newDesc !== undefined
                  ? {
                      description: newDesc,
                      ...(row.lastModified && {
                        lastModified: row.lastModified,
                      }),
                      ...(row.updatedAt && { updatedAt: row.updatedAt }),
                    }
                  : {
                      category: row.category,
                      subCategory: row.subCategory,
                      year: row.year,
                      month: row.month,
                      lastModified: row.lastModified,
                      updatedAt: row.updatedAt,
                    };

              // Recompute displayName when metadata (category/subcategory) changes (only if using subcategory naming)
              if (
                newDesc === undefined &&
                !useOriginalFileName &&
                !useBackendFileName
              ) {
                const mergedRow = { ...row, ...patch };
                patch.displayName = resolveDisplayFileName(
                  mergedRow,
                  value,
                  getSubCategoryName,
                );
              }

              setRowOverrides((prev) => ({
                ...prev,
                [key]: { ...(prev[key] || {}), ...patch },
              }));

              // Keep the sidebar data in sync so the panel reflects the save
              setDetailsRow((prev) =>
                prev && getRowKey(prev) === key ? { ...prev, ...patch } : prev,
              );
            }
          }}
        />
      )}
    </>
  );
}
